import React, { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../utils/api';
import { categoryIcon } from '../utils/categoryStyle';

export default function TransferReviewList({ onDismiss, onResolved }) {
  const [transactions, setTransactions] = useState(null);
  const [error, setError] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getRecentTransfers();
      setTransactions(data.transactions);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markNotTransfer = async (t) => {
    setResolvingId(t.id);
    try {
      await api.markNotTransfer(t.description);
      setTransactions((prev) => prev.filter((tx) => tx.id !== t.id));
      onResolved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setResolvingId(null);
    }
  };

  const confirmTransfer = async (t) => {
    setResolvingId(t.id);
    try {
      await api.confirmTransfer(t.id);
      setTransactions((prev) => prev.filter((tx) => tx.id !== t.id));
      onResolved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div style={styles.panel}>
      <p style={styles.flaggedBanner}>
        ⚠️ DO THIS: these are from the last 7 days and were automatically excluded from your spending because they
        looked like a transfer between your own accounts. If that's correct (like genuine credit card autopay),
        click Confirm to clear it from this list. If it's actually a real purchase or payment to someone else,
        mark it "Not a Transfer" so it counts as spending - this fixes it permanently for that merchant.
      </p>

      {error && <p style={styles.errorText}>{error}</p>}

      {transactions === null ? (
        <p style={styles.empty}>Loading...</p>
      ) : transactions.length === 0 ? (
        <p style={styles.empty}>Nothing to review right now.</p>
      ) : (
        <div style={styles.list}>
          {transactions.map((t) => (
            <div key={t.id} style={styles.row}>
              <div style={styles.rowInfo}>
                <div style={styles.rowDesc}>{categoryIcon(t.category?.[0])} {t.description}</div>
                <div style={styles.rowMeta}>
                  {new Date(`${t.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &middot; {fmt.currency(t.amount)}
                  {t.category?.length > 0 && <> &middot; {t.category.join(' / ')}</>}
                </div>
              </div>
              <div style={styles.rowActions}>
                <button
                  style={styles.confirmBtn}
                  onClick={() => confirmTransfer(t)}
                  disabled={resolvingId === t.id}
                  title="Yes, this really is a transfer (e.g. credit card autopay)"
                >
                  {resolvingId === t.id ? '...' : '✓ Confirm'}
                </button>
                <button
                  style={styles.fixBtn}
                  onClick={() => markNotTransfer(t)}
                  disabled={resolvingId === t.id}
                  title="This is a real purchase or payment, not an internal transfer"
                >
                  {resolvingId === t.id ? '...' : 'Not a Transfer'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button style={styles.dismissBtn} onClick={onDismiss}>
        ← View all transactions instead
      </button>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  flaggedBanner: {
    color: '#ef4444',
    fontSize: '0.82rem',
    fontWeight: '600',
    lineHeight: '1.55',
    margin: '0 0 1.1rem 0',
    fontFamily: "'Syne', sans-serif",
  },
  errorText: { color: '#ef4444', fontSize: '0.85rem' },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.75rem 0.9rem',
  },
  rowInfo: { minWidth: 0 },
  rowDesc: { color: '#e5e7eb', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Syne', sans-serif" },
  rowMeta: { color: '#6b7280', fontSize: '0.75rem', marginTop: '2px', fontFamily: "'DM Mono', monospace" },
  fixBtn: {
    background: 'rgba(0,212,170,0.12)',
    border: '1px solid rgba(0,212,170,0.4)',
    color: '#00d4aa',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: "'Syne', sans-serif",
  },
  rowActions: { display: 'flex', gap: '0.5rem', flexShrink: 0 },
  confirmBtn: {
    background: 'transparent',
    border: '1px solid #374151',
    color: '#9ca3af',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: "'Syne', sans-serif",
  },
  dismissBtn: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.78rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    textAlign: 'center',
    marginTop: '1.1rem',
  },
};
